import { useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';

import { HeaderStyles, Images, PowerIcon, cn } from 'scbk-ui-web';

// hooks
import {
  EventType,
  Languages,
  Modules,
  useConfig,
  useCurrentMenu,
  useEventBusEmit,
  useLocale,
  useMenuRouter,
  useMenuStore,
  useSecureContext,
} from 'scbk-services';
import { useLogout, useSession } from 'scbk-shared-module/modules/Session';

// types
import { useTranslation } from 'react-i18next';
import { usePageMove } from 'scbk-shared-module/hooks';
import type { MenuItem } from 'scbk-shared-module/layout/types/MenuItem';
import { Dialog } from 'scbk-shared-module/modules/GlobalComponents';

type headerBg = 'type1' | 'type2';

interface DesktopHeaderProps {
  menuitems: MenuItem[];
  headerType?: headerBg;
  className?: string;
  isScrolled?: boolean;
}

const DesktopHeader = ({ menuitems }: DesktopHeaderProps) => {
  const { goMenu } = useMenuRouter();
  const { goMain, goLogin } = usePageMove();
  const { isLogin } = useSession();
  const { doLogout } = useLogout();
  const { getConfig } = useConfig();
  const { getLocale } = useLocale();
  const emit = useEventBusEmit(); //이벤트버스
  const siteMapTree = useMenuStore((state) => state.siteMapTree); // siteMapTree
  const { currentMenuId } = useCurrentMenu(); // 현재 menu
  const { resetContext } = useSecureContext();

  // 다국어처리
  const { t } = useTranslation();

  // state
  const [openMenuId, setOpenMenuId] = useState<string | null>(null);
  const [activeParentMenuId, setActiveParentMenuId] = useState<string | null>(null);

  // ref
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  const activeParentMenuIdRef = useRef<string>(); // 활성화된 부모 메뉴
  const signOutBtnRef = useRef<HTMLButtonElement>(null); // 로그아웃 버튼 ref
  const clickLockedRef = useRef(false);

  const handleMouseEnter = (menuId: string) => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    if (openMenuId !== menuId) clickLockedRef.current = false;
    setOpenMenuId(menuId);
  };

  const handleMouseLeave = () => {
    if (clickLockedRef.current) return;
    timeoutRef.current = setTimeout(() => {
      setOpenMenuId(null);
    }, 200);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      clickLockedRef.current = false;
      setOpenMenuId(null);
    }
  };

  const handleMenuId = (menuId: string) => {
    const isSame = openMenuId === menuId && clickLockedRef.current;
    clickLockedRef.current = !isSame;
    setOpenMenuId(isSame ? null : menuId);
  };

  // 메뉴 이동
  const handleMenuClick = useCallback(
    async (menuInfo: MenuItem, menuDepth: number) => {
      // 일단은 2depth 메뉴에 subitem이 하나만 있고 href가 없는 경우가 menu.xml에 존재
      if (menuDepth === 1) {
        console.log('### menuInfo : ', menuInfo);
        const isTransfer = menuInfo.id.startsWith('TRN');
        if (isTransfer) {
          goMain({ type: 'TRANSFER', menuOptions: { refresh: true } });
        }
      } else if (menuDepth === 2 && menuInfo.subitems.length === 1 && !menuInfo.href) {
        //[2026.05.02 최성돈] PC뱅킹 전체메뉴에서 진입시 스택 초기화 처리
        goMenu(`${menuInfo.subitems[0].id}`, { historyOptions: { clearHistory: true } });
      } else {
        //[2026.05.02 최성돈] PC뱅킹 전체메뉴에서 진입시 스택 초기화 처리
        goMenu(`${menuInfo.id}`, { historyOptions: { clearHistory: true } });
      }
    },
    [goMenu, goMain, resetContext],
  );

  const handleGoNotice = () => {
    const homePageDomain = getConfig(Modules.COMMON).homePageDomain;
    const noticeDtlId = getLocale() === Languages.KO ? '2' : '16';

    // 새탭에서 열기
    window.open(`${homePageDomain}/np/kr/cm/cm/IndexNoticeBoard2.jsp?DtlId=${noticeDtlId}`);
  };

  /**
   * 고객센터 이동
   */
  const goCustomerCenter = () => {
    const homePageDomain = getConfig(Modules.COMMON).homePageDomain;

    const url =
      getLocale() === 'en' ? '/np/en/cm/cc/EnContactus.jsp' : '/np/kr/cm/cc/ContactCenterInfo.jsp';

    // location.href = `${homePageDomain}/3rdParty/portal_ascp.jsp?loginDt=&ascpURL=${url}&QueryString=&loginCheck=Y&certLogin=Y&toGB=ib&IBType=4&langCode=${getLocale().toUpperCase()}&secureData=`;
    // location.href = `${homePageDomain}/${url}`;
    window.open(`${homePageDomain}/${url}`);
  };

  /**
   * 로그아웃 처리
   */
  const onHandleLogout = useCallback(async () => {
    const isOk = await Dialog.confirm({
      title: '',
      content: t('Layout.DesktopHeader.012', {
        defaultValue: '로그아웃 할까요?',
      }),
      okBtnLabel: t('Layout.DesktopHeader.013', {
        defaultValue: '예',
      }),
      cancelBtnLabel: t('Layout.DesktopHeader.014', {
        defaultValue: '아니오',
      }),
    });

    if (!isOk) {
      signOutBtnRef.current?.focus();
      return;
    }

    doLogout({
      onLogout: async () => {
        resetContext();
        goLogin({ isGoPreLogin: true });
      },
    });
  }, [resetContext]);

  useEffect(() => {
    // 부모메뉴 활성화를 위한
    if (activeParentMenuIdRef.current) {
      return;
    }

    if (!siteMapTree) {
      return;
    }

    if (!currentMenuId) {
      return;
    }

    const rootMapTree = { ...siteMapTree['ROOT']?.children };

    activeParentMenuIdRef.current = Object.keys(rootMapTree).find((key) =>
      Object.values({ ...rootMapTree[key]?.children }).find((item) => item.id === currentMenuId),
    );
    setActiveParentMenuId(activeParentMenuIdRef.current ?? null);
  }, [siteMapTree, currentMenuId]);

  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  useEffect(() => {
    // 외부 클릭시 메뉴 닫기
    const handleClickOutside = (e: MouseEvent) => {
      const nav = document.querySelector('nav');
      if (nav && !nav.contains(e.target as Node)) {
        clickLockedRef.current = false;
        setOpenMenuId(null);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  return (
    <>
      <div className={cn(HeaderStyles['desktop-nav-container'])}>
        {/* 헤더 - nav */}
        <div className={`${HeaderStyles['desktop-nav-wrap']}`}>
          <div className={HeaderStyles['desktop-nav-inner']}>
            <button
              onClick={() => {
                goMain({ type: 'HOME' });
                // goMenu('SAMPLE0000');
              }}
            >
              <span className={HeaderStyles['logo']}>
                <img
                  src={Images.Logo}
                  alt={t('Layout.DesktopHeader.001', {
                    defaultValue: 'Standard chartered sc제일은행',
                  })}
                />
              </span>
            </button>
            <nav
              className={HeaderStyles['desktop-nav-items']}
              aria-label={t('Layout.DesktopHeader.002', {
                defaultValue: '대메뉴',
              })}
              id="navigation"
            >
              <ul className={HeaderStyles['desktop-nav-items-inner']}>
                {menuitems.map((menu) => {
                  const isOpen = openMenuId === menu.id;
                  const isActive = isOpen || (!openMenuId && activeParentMenuId === menu.id);
                  return (
                    <li
                      key={menu.id}
                      className="px-[32px] whitespace-nowrap" //260812 클래스추가 , 260819 클래스 삭제
                      onMouseLeave={handleMouseLeave}
                      onMouseEnter={() => handleMouseEnter(menu.id)}
                    >
                      <button
                        type="button"
                        aria-expanded={openMenuId === menu.id}
                        onKeyDown={handleKeyDown}
                        onClick={() => {
                          handleMenuId(menu.id);
                        }}
                        className={`${HeaderStyles['desktop-nav-tit']} ${isActive ? HeaderStyles.active : ''}`}
                        // 2026.08.14 권태민 웹접근성 관련 : 1depth 링크 삭제
                        // onClick={() => {
                        //   handleMenuClick(menu, 1);
                        // }}
                      >
                        <span className="text-center">{menu.label}</span>
                      </button>
                      <div
                        className={`absolute top-[85px] left-0 right-0 w-[calc(100% - 16px)] lef-[50%] bg-white shadow-[0_4px_10px_rgba(0,0,0,0.04)] transition-all duration-200 ${isOpen ? 'block' : 'hidden pointer-events-none'}`}
                      >
                        <div className="w-[1000px] mx-auto pt-8 pb-10">
                          <div className="relative flex">
                            <ul className="grid grid-cols-4 gap-y-6 gap-x-10 pl-[157px]">
                              {menu.subitems.map((subitem) => (
                                <li
                                  key={subitem.id}
                                  className="body-md text-basic-03 hover:text-primary-04 hover:font-weight-medium min-w-[180px]"
                                >
                                  <Link
                                    to={'javascript:void(0);'}
                                    key={subitem.id}
                                    onClick={() => {
                                      setOpenMenuId(null);
                                      handleMenuClick(subitem, 2);
                                    }}
                                    className="block break-words whitespace-normal"
                                  >
                                    {subitem.label}
                                  </Link>
                                </li>
                              ))}
                            </ul>
                          </div>
                        </div>
                      </div>
                    </li>
                  );
                })}
              </ul>
            </nav>
            <div className={cn(HeaderStyles['desktop-prelogin-info'])}>
              {getLocale() !== 'en' && (
                <button
                  className={HeaderStyles['link-txt']}
                  title={t('Layout.DesktopHeader.003', {
                    defaultValue: '새창열림',
                  })}
                  onClick={(e) => {
                    emit(EventType.SpeedSearchPopupRequest, e);
                  }}
                >
                  {t('Layout.DesktopHeader.004', {
                    defaultValue: '스피드조회',
                  })}
                </button>
              )}
              <button
                className={HeaderStyles['link-txt']}
                title={t('Layout.DesktopHeader.005', {
                  defaultValue: '새창열림',
                })}
                onClick={() => {
                  handleGoNotice();
                }}
              >
                {t('Layout.DesktopHeader.006', {
                  defaultValue: '공지사항',
                })}
              </button>
              <button
                className={HeaderStyles['link-txt']}
                title={t('Layout.DesktopHeader.007', {
                  defaultValue: '바로가기',
                })}
                onClick={() => {
                  goCustomerCenter();
                }}
              >
                {t('Layout.DesktopHeader.008', {
                  defaultValue: '고객센터',
                })}
              </button>
              {/* 프리로그인이 아니고 로그인 상태일때만 */}
              {isLogin() && currentMenuId !== 'LGNADV000' && (
                <button
                  className="com-box-border w-8 h-8 ml-2"
                  onClick={() => {
                    onHandleLogout();
                  }}
                  aria-label={t('Layout.DesktopHeader.009', {
                    defaultValue: '로그아웃',
                  })}
                  ref={signOutBtnRef}
                >
                  <PowerIcon width={16} height={16} />
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export { DesktopHeader };
